/**
 * Spoken lines (story q-demo-voice-lines, AD-759/AD-861): which voice cue a shown line plays, and
 * the one-line-at-a-time sequencer. General for any speaker in the main game: the map is data
 * (content/audio/voice-lines.json -> voice.generated.json), keyed by NPC id / quest id / enemy name;
 * nothing here knows the guest demo. Pure: the engine is injected, so this is unit-tested in Node.
 *
 * AD-870 (owner, 2026-10-28): opening a conversation speaks only the NPC's greeting (its
 * catchphrase). Every other line is the answer to a player choice: asking about a quest speaks its
 * pitch, accept / decline their own lines, a "tell me about…" topic its lines, asking after a held
 * quest its progress line, a turn-in its completion line, goodbye the farewell.
 *
 * Subtitles are the text the UI already shows. A cue plays only when the text on screen is the
 * text it was rendered from (`texts`), so audio and subtitle can never disagree; a line with no
 * recording yet has no cue, so its subtitle shows and nothing plays.
 */

/** The quest beats a giver answers player choices with (AD-870), keyed in the voice map. */
export type QuestBeat = 'offer' | 'accept' | 'decline' | 'progress' | 'complete';

export interface VoiceMap {
  /**
   * NPC id -> its cues: the greeting (spoken on open), the farewell, its flavour/topic lines (by
   * index) and, per quest id, one cue per quest beat.
   */
  npcs: Record<
    string,
    {
      greeting?: string;
      farewell?: string;
      lines?: string[];
      quests?: Record<string, Partial<Record<QuestBeat, string>>>;
    }
  >;
  /** Enemy display name (the wire carries a name, never a content id) -> bark cues. */
  enemies: Record<string, { aggro?: string; phase?: string; defeat?: string }>;
  /** Cue id -> the exact text it speaks. */
  texts: Record<string, string>;
  /** Cue id -> the speaker's display name (for a bark subtitle). */
  speakers: Record<string, string>;
}

export interface TalkLike {
  npcId: string;
  greeting: string | null;
  lines: string[];
  offers: { questId: string; offerText: string | null }[];
  /** "Tell me about…" options (q-npc-lore-talk-option): the line indexes each one says. */
  topics?: readonly { id: string; lines: readonly number[] }[];
  /** Said on goodbye (AD-870). */
  farewell?: string;
  /** Per quest id, the lines its choices are answered with (AD-870). */
  questLines?: Readonly<Record<string, Partial<Record<Exclude<QuestBeat, 'offer'>, string>>>>;
}

/** A player choice in a conversation; each one may speak one line (or a topic's lines). AD-870. */
export type VoiceChoice =
  | { kind: QuestBeat; questId: string }
  | { kind: 'topic'; topicId: string }
  | { kind: 'farewell' };

/** The line indexes some topic owns: never shown or spoken unasked (q-npc-lore-talk-option). */
export function topicOwnedLines(d: Pick<TalkLike, 'topics'>): Set<number> {
  const owned = new Set<number>();
  for (const t of d.topics ?? []) for (const i of t.lines) owned.add(i);
  return owned;
}

/**
 * The text a choice answers with, as the dialogue box shows it (its subtitle): the quest's pitch
 * for `offer`, the quest's own line for the other beats, the NPC's farewell. Null when the content
 * has none. A topic's text is its lines (`dialogueView`), so it is not a single line here.
 */
export function choiceText(d: TalkLike, choice: VoiceChoice): string | null {
  switch (choice.kind) {
    case 'offer':
      return d.offers.find((o) => o.questId === choice.questId)?.offerText ?? null;
    case 'accept':
    case 'decline':
    case 'progress':
    case 'complete':
      return d.questLines?.[choice.questId]?.[choice.kind] ?? null;
    case 'farewell':
      return d.farewell ?? null;
    case 'topic':
      return null;
  }
}

/**
 * The cues to speak when a conversation OPENS (AD-870): the greeting and nothing else — no quest
 * pitch, flavour line or topic line speaks until the player chooses it. Silent when the greeting
 * on screen differs from the rendered text, and when it was already heard this session (`heard`,
 * q-npc-lore-talk-option: re-opening a conversation does not re-narrate it).
 */
export function playlistFor(
  map: VoiceMap,
  d: TalkLike,
  heard: ReadonlySet<string> = new Set(),
): string[] {
  const cue = map.npcs[d.npcId]?.greeting;
  if (cue === undefined || d.greeting == null || map.texts[cue] !== d.greeting) return [];
  return heard.has(cue) ? [] : [cue];
}

/**
 * The cues a chosen "tell me about…" topic speaks: its lines in order, each only when the text
 * on screen is the text it was rendered from. A line with no rendered voice yet stays silent (its
 * subtitle still shows).
 */
export function topicPlaylist(map: VoiceMap, d: TalkLike, topicId: string): string[] {
  const npc = map.npcs[d.npcId];
  const topic = d.topics?.find((t) => t.id === topicId);
  if (!npc || !topic) return [];
  const out: string[] = [];
  for (const i of topic.lines) {
    const cue = npc.lines?.[i];
    const text = d.lines[i];
    if (cue !== undefined && text !== undefined && map.texts[cue] === text) out.push(cue);
  }
  return out;
}

/**
 * The cues one player choice speaks (AD-870): a topic's lines, or the single line the choice
 * answers with, when it has a recording made from exactly the text on screen.
 */
export function choicePlaylist(map: VoiceMap, d: TalkLike, choice: VoiceChoice): string[] {
  if (choice.kind === 'topic') return topicPlaylist(map, d, choice.topicId);
  const npc = map.npcs[d.npcId];
  if (!npc) return [];
  const cue =
    choice.kind === 'farewell' ? npc.farewell : npc.quests?.[choice.questId]?.[choice.kind];
  const text = choiceText(d, choice);
  return cue !== undefined && text != null && map.texts[cue] === text ? [cue] : [];
}

/**
 * The page's conversations, spoken: an open speaks the greeting once a session
 * (q-npc-lore-talk-option, Owner Desk #279), and each player choice speaks its own answer (AD-870).
 */
export class ConversationVoice {
  private readonly heard = new Set<string>();

  constructor(
    private readonly map: VoiceMap,
    private readonly sequencer: VoiceSequencer,
  ) {}

  /** A conversation opened (`seq` = its talk number; the same seq again is a no-op). */
  open(seq: number, d: TalkLike): void {
    this.sequencer.show(seq, playlistFor(this.map, d, this.heard), this.mark);
  }

  /** The player made a choice: speak its answer now, replacing whatever was speaking. */
  choose(d: TalkLike, choice: VoiceChoice): void {
    this.sequencer.say(choicePlaylist(this.map, d, choice), this.mark);
  }

  /** The player chose a "tell me about…" topic (asked again, it is said again). */
  ask(d: TalkLike, topicId: string): void {
    this.choose(d, { kind: 'topic', topicId });
  }

  /**
   * A cue counts as heard once the engine starts it (or voice is muted); one cut before its turn,
   * or refused because audio was still locked, speaks next time (q-voice-locked-cue-counted-heard).
   */
  private readonly mark = (cue: string): void => {
    this.heard.add(cue);
  };

  stop(): void {
    this.sequencer.stop();
  }
}

export interface VoiceSink {
  /**
   * Speak one cue on the voice bus; `onEnd` fires when it finishes (or cannot play). Returns the
   * engine's outcome (`AudioEngine.speak`'s `CueOutcome`): whether the line actually started.
   */
  speak(cue: string, onEnd: () => void): string;
  /** Cut the line being spoken, if any. */
  stopVoice(): void;
}

/**
 * The speak outcomes that count a line as HEARD (q-voice-locked-cue-counted-heard): it started
 * (`loading` then `played`), or the player muted voice (their choice). `locked` (audio not yet
 * unlocked by the first tap), `missing` and `unknown` were never heard, so the line speaks again
 * on the next open.
 */
export const HEARD_OUTCOMES: ReadonlySet<string> = new Set(['loading', 'played', 'muted']);

/** Gap between consecutive lines of one conversation. */
export const LINE_GAP_MS = 300;

type Timer = ReturnType<typeof setTimeout>;

export class VoiceSequencer {
  private token = 0;
  private lastSeq = -1;
  private timer: Timer | null = null;

  constructor(
    private readonly sink: VoiceSink,
    private readonly later: (fn: () => void, ms: number) => Timer = (f, ms) => setTimeout(f, ms),
    private readonly cancel: (t: Timer) => void = (t) => clearTimeout(t),
  ) {}

  /**
   * Start a conversation's playlist. `seq` is the talk's sequence number: a panel that re-mounts
   * with the same stale dialogue does not replay it. Replaces whatever was speaking.
   */
  show(seq: number, cues: readonly string[], onSpoken?: (cue: string) => void): boolean {
    if (seq === this.lastSeq) return false;
    this.lastSeq = seq;
    return this.say(cues, onSpoken);
  }

  /**
   * Speak `cues` now, replacing whatever was speaking: the player asked for it (a chosen
   * "tell me about…" topic), so no sequence guard applies. `onSpoken` hears each cue the engine
   * actually started (`HEARD_OUTCOMES`) — a line cut before its turn, or one that could not play
   * because audio was still locked, was never said.
   */
  say(cues: readonly string[], onSpoken?: (cue: string) => void): boolean {
    this.stop();
    if (cues.length === 0) return false;
    const mine = this.token;
    const next = (i: number): void => {
      if (mine !== this.token || i >= cues.length) return;
      const cue = cues[i] as string;
      const outcome = this.sink.speak(cue, () => {
        if (mine !== this.token) return;
        if (i + 1 < cues.length) this.timer = this.later(() => next(i + 1), LINE_GAP_MS);
      });
      if (HEARD_OUTCOMES.has(outcome)) onSpoken?.(cue);
    };
    next(0);
    return true;
  }

  /** Close / advance: cut the line and drop the rest of the playlist. */
  stop(): void {
    this.token += 1;
    if (this.timer !== null) this.cancel(this.timer);
    this.timer = null;
    this.sink.stopVoice();
  }
}
